import { useState, useEffect } from "react";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

type Language = "nl" | "en";

const LanguageSwitcher = () => {
  const [language, setLanguage] = useState<Language>("nl");

  // Load language preference from localStorage
  useEffect(() => {
    const savedLang = localStorage.getItem("language") as Language | null;
    if (savedLang && (savedLang === "nl" || savedLang === "en")) {
      setLanguage(savedLang);
      applyLanguage(savedLang);
    }
  }, []);

  const applyLanguage = (lang: Language) => {
    if (lang === "en") {
      // Add Google Translate script if not already present
      if (!document.getElementById("google-translate-script")) {
        const script = document.createElement("script");
        script.id = "google-translate-script";
        script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        document.body.appendChild(script);

        // Initialize Google Translate
        (window as any).googleTranslateElementInit = () => {
          new (window as any).google.translate.TranslateElement(
            {
              pageLanguage: "nl",
              includedLanguages: "en",
              autoDisplay: false,
            },
            "google_translate_element"
          );
          // Auto-select English
          setTimeout(() => {
            const selectEl = document.querySelector(".goog-te-combo") as HTMLSelectElement;
            if (selectEl) {
              selectEl.value = "en";
              selectEl.dispatchEvent(new Event("change"));
            }
          }, 500);
        };
      } else {
        // If script already loaded, just change language
        const selectEl = document.querySelector(".goog-te-combo") as HTMLSelectElement;
        if (selectEl) {
          selectEl.value = "en";
          selectEl.dispatchEvent(new Event("change"));
        }
      }
    } else {
      // Reset to Dutch - remove translation
      const frame = document.querySelector(".goog-te-banner-frame") as HTMLIFrameElement;
      if (frame) {
        const closeBtn = frame.contentDocument?.querySelector(".goog-close-link") as HTMLElement;
        closeBtn?.click();
      }
      // Also try cookie-based reset
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=." + window.location.hostname;
      // Reload to fully reset if needed
      if (document.querySelector(".goog-te-combo")) {
        window.location.reload();
      }
    }
  };

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
    localStorage.setItem("language", newLang);
    applyLanguage(newLang);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1 px-2">
          <Globe className="w-4 h-4" />
          <span className="text-xs font-medium uppercase">{language}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[120px]">
        <DropdownMenuItem
          onClick={() => handleLanguageChange("nl")}
          className={language === "nl" ? "bg-accent" : ""}
        >
          <span className="mr-2">🇳🇱</span>
          Nederlands
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleLanguageChange("en")}
          className={language === "en" ? "bg-accent" : ""}
        >
          <span className="mr-2">🇬🇧</span>
          English
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
